import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Bell, Settings, RefreshCw, Fuel, Radio, Search, Filter, CheckCheck, Trash2 } from 'lucide-react';
import { useCompanyContext } from '@/hooks/use-company-context';
import { useHasGpsTracking } from '@/hooks/useHasGpsTracking';
import { useFleetNotifications } from '@/hooks/useFleetNotifications';
import { NotificationItem } from '@/components/fleet/NotificationItem';

export default function FleetNotifications() {
  const { slug } = useParams<{ slug: string }>();
  const { companyId } = useCompanyContext();
  const hasGps = useHasGpsTracking(companyId);
  const {
    notifications,
    stats,
    isLoading,
    filterByType,
  } = useFleetNotifications(companyId);
  
  const [activeTab, setActiveTab] = useState<'all' | 'markings' | 'gps' | 'system'>('all');
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filtered notifications
  const filteredNotifications = useMemo(() => {
    let filtered = notifications;
    
    // Filter by tab
    if (activeTab !== 'all') {
      filtered = filterByType(activeTab === 'markings' ? 'marking' : activeTab);
    }
    
    // Filter unread
    if (showUnreadOnly) {
      filtered = filtered.filter(n => !n.isRead);
    }
    
    // Filter by search
    if (searchQuery) {
      filtered = filtered.filter(n => 
        n.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        n.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    return filtered;
  }, [notifications, activeTab, showUnreadOnly, searchQuery, filterByType]);
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Bell className="h-8 w-8" />
            Notificações
          </h1>
          <p className="text-muted-foreground mt-1">
            Histórico completo de notificações da frota e GPS
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to={`/company/${slug}/admin/systems/fleet/notifications/preferences`}>
              <Settings className="h-4 w-4 mr-2" />
              Preferências
            </Link>
          </Button>
          <Button variant="outline" size="icon">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">Total</div>
          <div className="text-2xl font-bold">{stats.total}</div>
          <p className="text-xs text-muted-foreground">
            {stats.unread} não lidas
          </p>
        </Card>
        
        <Card className="p-4">
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Fuel className="h-4 w-4" />
            Marcações
          </div>
          <div className="text-2xl font-bold">{stats.markings.total}</div>
          <p className="text-xs text-muted-foreground">
            {stats.markings.fuel} combustível, {stats.markings.odometer} km
          </p>
        </Card>
        
        {hasGps && (
          <Card className="p-4">
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Radio className="h-4 w-4 text-green-600" />
              GPS
            </div>
            <div className="text-2xl font-bold">{stats.gps.total}</div>
            <p className="text-xs text-destructive">
              {stats.gps.failed} falhas
            </p>
          </Card>
        )}
        
        <Card className="p-4">
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Sistema
          </div>
          <div className="text-2xl font-bold">{stats.system.total}</div>
          <p className="text-xs text-muted-foreground">
            {stats.system.unread} não lidas
          </p>
        </Card>
      </div>
      
      {/* Tabs + Filters */}
      <Card>
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
          <div className="border-b p-4">
            <div className="flex items-center justify-between mb-4">
              <TabsList>
                <TabsTrigger value="all">Todas ({stats.total})</TabsTrigger>
                <TabsTrigger value="markings">
                  <Fuel className="h-4 w-4 mr-2" />
                  Marcações ({stats.markings.total})
                </TabsTrigger>
                {hasGps && (
                  <TabsTrigger value="gps">
                    <Radio className="h-4 w-4 mr-2" />
                    GPS ({stats.gps.total})
                  </TabsTrigger>
                )}
                <TabsTrigger value="system">
                  <Bell className="h-4 w-4 mr-2" />
                  Sistema ({stats.system.total})
                </TabsTrigger>
              </TabsList>
              
              <div className="flex items-center gap-2">
                <Switch
                  checked={showUnreadOnly}
                  onCheckedChange={setShowUnreadOnly}
                />
                <span className="text-sm">Apenas não lidas</span>
              </div>
            </div>
            
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar notificações..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">
                    <Filter className="h-4 w-4 mr-2" />
                    Ações
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem>
                    <CheckCheck className="h-4 w-4 mr-2" />
                    Marcar todas como lidas
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Limpar antigas (30+ dias)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          
          {/* Notifications List */}
          <TabsContent value={activeTab} className="p-4 space-y-3">
            {isLoading ? (
              <div className="text-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">Carregando...</p>
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Bell className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Nenhuma notificação encontrada</p>
              </div>
            ) : (
              <ScrollArea className="h-[600px] pr-4">
                {filteredNotifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                  />
                ))}
              </ScrollArea>
            )}
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}
